/**
 * /app/trusts — Trust list page (cross-trust home for multi-trust users).
 *
 * Driven by useTrusts() — returns every trust the calling user is a
 * member of (settlor / trustee / beneficiary). FICA status derives
 * client-side from ficaDaysOut.
 */

import { useState } from 'react';
import { displayLegalName } from '../../../lib/format/legal-name';
import { displayTrustName } from '../../../lib/format/trust-name';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ScreenShell } from '../../../components/screens/screen-shell';
// ── COHESION PASS, SCREEN 3a (2026-09-15) ───────────────────────────────────
// The three shared primitives phase 2 built, doing the job this screen used to
// do three separate ways. See the block comment on TrustListScreen below.
import { ConsoleBand } from '../../../components/admin/console-band';
import { BleedTable, type BleedColumn, type BleedRow } from '../../../components/bleed-table';
import { StateCard } from '../../../components/state-card';
import { Icon } from '../../../components/icon';
import { Modal } from '../../../components/ui/modal';
import { ValueProvenance, formatAsAtShort } from '../../../components/value-provenance';
import { useToastStore } from '../../../lib/toast-store';
import {
  useTrusts,
  useCreateTrust,
  useTenantMe,
  canReadSurface,
  type TrustSummary,
} from '../../../lib/api';
import { formatRand } from '../../../lib/fmt-r';
import { registrationDisplay } from '../../../lib/format/registration';
import { stewardshipOf } from '../../../lib/firm-stewardship';
import { mapError } from '../../../lib/error-messages';
import { ficaDaysOutOf } from '../../../lib/fica-review';
import { useActAsClientName } from '../../../lib/acting/context';

export const Route = createFileRoute('/app/trusts/')({
  component: TrustListScreen,
});

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-3)',
  display: 'block',
  marginBottom: 6,
};

/**
 * Add-a-trust modal — self-serve create flow (Task 6.6).
 *
 * Reuses the three core onboarding trust fields (name / Master registration /
 * deed date) that `POST /v1/trusts` accepts. The trust is created in draft
 * state with the caller as the first member; on success we toast and navigate
 * straight into the new trust's overview so the user can keep building it out
 * (add holdings, beneficiaries, etc.).
 */
function AddTrustModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { pushToast } = useToastStore();
  const createTrust = useCreateTrust();

  const [name, setName] = useState('');
  const [registration, setRegistration] = useState('');
  const [deedDate, setDeedDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setRegistration('');
    setDeedDate('');
    setError(null);
    createTrust.reset();
  }

  function close() {
    reset();
    onClose();
  }

  const trimmedName = name.trim();
  const trimmedReg = registration.trim();
  const canSubmit =
    trimmedName.length > 0 && trimmedReg.length > 0 && deedDate.length > 0;

  function handleCreate() {
    setError(null);
    if (!trimmedName) {
      setError('Enter the trust name as registered with the Master.');
      return;
    }
    if (!trimmedReg) {
      setError('Enter the Master registration / IT number.');
      return;
    }
    if (!deedDate) {
      setError('Choose the date the trust deed was signed.');
      return;
    }
    createTrust.mutate(
      { name: trimmedName, registration: trimmedReg, deedDate },
      {
        onSuccess: (res) => {
          pushToast({ kind: 'success', message: `${displayLegalName(res.trust.name)} added.` });
          close();
          void navigate({ to: '/app/trusts/$id', params: { id: res.trust.id } });
        },
        onError: (err) => {
          if (err.isStepUpRequired) {
            setError('Step-up authentication is required to add a trust.');
            return;
          }
          setError(mapError(err, 'save'));
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={close} title="Add a trust" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 4 }}>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.55 }}>
          Add another trust you administer. It starts as a draft with you as the
          first member. You can record trustees, beneficiaries and assets once
          it's created.
        </div>
        <div>
          <label htmlFor="add-trust-name" style={fieldLabelStyle}>
            Trust name
          </label>
          <input
            id="add-trust-name"
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Trust name as registered"
            disabled={createTrust.isPending}
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label htmlFor="add-trust-reg" style={fieldLabelStyle}>
            Master registration number
          </label>
          <input
            id="add-trust-reg"
            type="text"
            value={registration}
            onChange={(e) => setRegistration(e.target.value)}
            placeholder="Master IT number"
            disabled={createTrust.isPending}
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label htmlFor="add-trust-deed" style={fieldLabelStyle}>
            Trust deed date
          </label>
          <input
            id="add-trust-deed"
            type="date"
            value={deedDate}
            onChange={(e) => setDeedDate(e.target.value)}
            disabled={createTrust.isPending}
            style={fieldInputStyle}
          />
        </div>
        {error !== null && (
          <div
            role="alert"
            aria-live="polite"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--negative-soft)',
              border: '1px solid var(--negative-soft)',
              color: 'var(--negative)',
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}
      </div>
      <Modal.Footer>
        <button className="btn-secondary" onClick={close} disabled={createTrust.isPending}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleCreate}
          disabled={!canSubmit || createTrust.isPending}
        >
          {createTrust.isPending ? 'Adding…' : 'Add trust'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// 2026-05-20 (Batch 8 / F02-P-074): collapsed onto the shared formatRand
// from lib/fmt-r so every Rand cell uses the same narrow-space thousands
// separator and NBSP between R and the number.
function fmtR(n: number) {
  return formatRand(n);
}

function formatDeedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

// 2026-07-11 (first-touch rehearsal, finding #13): a review 30 days out
// isn't due — it's normal, upcoming business. The old 2-tier split (<=30 =
// alarming "FICA due" amber, >30 = calm "FICA current") put a compliance-
// failure badge on a brand-new trust that had done nothing wrong. The
// alarming tone is now reserved for daysOut <= 0 (due now / overdue); any
// review still in the future is calm, with a countdown label that matches
// the dashboard's own neutral "FICA review in N days" copy
// (components/dashboard/dashboard.tsx).
export type FicaBadgeTone = 'calm' | 'alarm';

export interface FicaBadge {
  bg: string;
  fg: string;
  label: string;
  tone: FicaBadgeTone;
}

// ── "FICA CURRENT" WAS "IN GOOD STANDING" WEARING A HAT (TRS-01, 2026-08-27) ──
//
// Beno, walking two real trusts on 22 August: *"although it says two trusts in
// good standing, it's not correct."* Not one FICA document existed for either.
// The words "in good standing" were removed that week. This pill was not, and it
// made the identical claim in green: a trust whose review date happened to be
// more than 30 days out was labelled FICA CURRENT in positive-green, on the card
// a client sees first.
//
// The pill only ever knew ONE thing — the distance to `nextFicaReview`, a date
// the backend stamps 30 days after creation whether or not anybody was ever
// verified (TRS-03). It never knew whether a single identity document existed.
// So it now says the thing it knows, in a neutral tone, and claims nothing about
// the file. Who is actually verified is stated person by person on the trust
// overview, which reads the register.
export function ficaBadgeFor(
  daysOut: number | undefined,
  initialFicaOnFile?: boolean | null,
): FicaBadge {
  // TRS-03 (grader, 5 Sep 2026): a trust whose register carries no verified
  // person has no review cycle. The pill names the work instead of a countdown.
  // `daysOut` is now UNDEFINED on such a trust — the backend withholds it — so
  // the pill has nothing to count down even if this guard were removed.
  if (initialFicaOnFile === false) {
    return {
      bg: 'var(--surface-2)',
      fg: 'var(--ink-2)',
      label: 'Initial FICA outstanding',
      tone: 'calm',
    };
  }
  if (daysOut === undefined) {
    // No clock and no "no initial FICA" fact: the review date could not be
    // read. A pill must not invent one, and must not go red.
    return {
      bg: 'var(--surface-2)',
      fg: 'var(--ink-2)',
      label: 'FICA review date not on file',
      tone: 'calm',
    };
  }
  if (daysOut <= 0) {
    return {
      bg: 'var(--negative-soft)',
      fg: 'var(--negative)',
      label: 'FICA overdue',
      tone: 'alarm',
    };
  }
  return {
    // Same flat/neutral pill treatment as `.perf-pill.flat` /
    // `.status-pill.completed` elsewhere in design-system.css — informative,
    // not alarming, and not a compliance verdict either.
    bg: 'var(--surface-2)',
    fg: 'var(--ink-2)',
    label: `FICA review in ${daysOut}d`,
    tone: 'calm',
  };
}


/**
 * TrustListScreen — screen 3a of the cohesion pass (2026-09-15).
 *
 * WHAT IT WAS. Two stacked cards in a `max-width: 720px` column, each 24px by
 * 28px with a 52px icon tile, the trust's value in ITALIC SERIF (an amount is
 * not a named thing), an uppercase `FICA REVIEW IN 12 DAYS` pill, and a grey
 * explanatory paragraph in a box of its own at the bottom. Two cards that wide
 * waste the viewport and give a reader no scanning axis; at ten trusts it is
 * unusable. GAP-ANALYSIS also counted THREE hand-rolled state blocks inside
 * nine lines of this one file, which is what made it the example the state card
 * was written against.
 *
 * WHAT IT IS. One card holding one full-bleed table: trust (italic serif over
 * its mono registration, severity in the 2px left marker) / deed / value /
 * FICA, one sentence-case chip / one action. The band carries the book total on
 * its own rule, and the sentence that used to sit in a box is the table's
 * footer line. Every state is one variant of `StateCard`.
 *
 * THE TWO THINGS A TABLE COULD HAVE QUIETLY LOST. GAP-ANALYSIS marks this
 * screen auth-adjacent for these, and `__tests__/index-cohesion-table.test.tsx`
 * pins both:
 *
 *   1. The VALUE COLUMN IS HIDDEN, not blanked, for a reader who cannot read a
 *      trust's `financials` surface. A column is one decision for the whole
 *      list where a card was a decision per trust, so the column renders when
 *      ANY listed trust is readable and the cell of a trust that is not
 *      readable renders NOTHING — no zero, no em dash, no "Not valued". A
 *      reader with no readable trust therefore sees no column at all, which is
 *      the behaviour the cards had: a row of empty cells where money belongs
 *      shows a beneficiary the shape of a figure they are not being given.
 *   2. `Not valued`, never `R 0,00`. A zero is a claim about the corpus.
 *
 * And `+ Add a trust` stays gated to a self-onboarding tenant, and still never
 * renders for a firm acting as the client.
 */
function TrustListScreen() {
  const { data, isLoading, isError, error } = useTrusts();
  const trusts = data?.trusts ?? [];
  const [addOpen, setAddOpen] = useState(false);

  // "Add a trust" is a b2c SELF-ONBOARD action — it only makes sense for a
  // self-managed individual account. A firm-managed member (trustee OR
  // beneficiary) whose trusts live in a firm's book must never see it: the
  // backend already blocks the write, but showing the button is a read-only-
  // experience leak. `self_managed` is true only for a b2c owner of a
  // non-firm-stewarded tenant (see tenants-me.ts). Absent/false → hidden
  // (fail-closed). While `useTenantMe()` is loading, `self_managed` is
  // undefined → button stays hidden until we positively know the account is
  // self-managed.
  const { data: tenantMe } = useTenantMe();
  const canSelfOnboard = tenantMe?.self_managed === true;
  // Who is reading this screen? A firm standing in for the client gets different
  // copy from the client themselves — see the empty state below.
  const actingFor = useActAsClientName();
  // A visiting firm never gets the account holder's own self-onboard action, on
  // a self-managed tenant or otherwise. Adding a trust to somebody's estate is
  // theirs to do; a firm files trusts through the Client Book, where the grant
  // and the audit trail live. One flag now, read by the topbar button and the
  // empty state's action, because the two had drifted apart once already.
  const canAdd = canSelfOnboard && !actingFor;
  // Copy that names a firm asks first whether a firm stewards this workspace
  // (NEW-R12-SELFMANAGED-FIRM-COPY: a self-managing client was told "your firm
  // grants you access").
  const stewardship = stewardshipOf(tenantMe);
  const joinsListLine = actingFor
    ? 'A trust joins this list once its documents have been read and you confirm the structure.'
    : stewardship === 'firm'
      ? 'A trust joins this list when your firm grants you access to it.'
      : stewardship === 'self'
        ? 'A trust joins this list when you add it.'
        : 'A trust joins this list when you are given access to it.';

  // See rule 1 in the header block. `canReadSurface` fails OPEN on an older
  // backend that sent no surfaces, so this is true by default.
  const showValueColumn = trusts.some((t) => canReadSurface(t, 'financials'));
  // The book total counts only what this reader may actually read, so the
  // figure on the band and the figures in the column always add up.
  const readableValued = trusts.filter(
    (t) => canReadSurface(t, 'financials') && t.totalValue !== undefined,
  );
  const bookTotal = readableValued.reduce((sum, t) => sum + (t.totalValue ?? 0), 0);

  const trustWord = (n: number) => `${n} trust${n !== 1 ? 's' : ''}`;

  // DESIGN GATE A2 — no number before the read settles. The title is a COUNT,
  // so while the list is loading (or has failed) it must not be one: a "0
  // trusts" headline is the exact thing that cost credibility in front of
  // Destinata, and this screen is one of the two places it appeared.
  const title = isLoading || isError ? 'Your trusts' : trustWord(trusts.length);
  /* The title is the count, so the sub must not be the count again: "1 trust"
     over "1 trust" is the same fact at two sizes. It says who administers
     them instead, which is the one thing `self_managed` actually tells us and
     the difference a reader cares about on this screen. */
  const sub = isLoading
    ? 'Reading your trusts…'
    : isError
      ? 'We could not reach the server.'
      : canSelfOnboard
        ? `${trustWord(trusts.length)} under your administration`
        : `${trustWord(trusts.length)}, administered by your firm`;

  const columns: BleedColumn[] = [
    { key: 'trust', header: 'Trust' },
    { key: 'deed', header: 'Deed' },
    ...(showValueColumn
      ? [{ key: 'value', header: 'Value', kind: 'num' as const }]
      : []),
    { key: 'fica', header: 'FICA' },
    // The action column's header is a spacer: it contributes width without
    // appearing to be a heading for a column of buttons.
    { key: 'open', header: 'Open the trust', kind: 'action' as const },
  ];

  const rows: BleedRow[] = trusts.map((trust) => trustRow(trust, showValueColumn));

  return (
    <ScreenShell
      activeNav="trusts"
      eyebrow="My trusts"
      title={title}
      sub={sub}
      topbarRight={
        canAdd ? (
          <div className="topbar-actions">
            <button className="btn-primary" onClick={() => setAddOpen(true)}>
              <Icon name="plus" size={13} />
              Add a trust
            </button>
          </div>
        ) : undefined
      }
    >
      <AddTrustModal open={addOpen} onClose={() => setAddOpen(false)} />

      {isLoading ? (
        <StateCard
          variant="loading"
          testId="trusts-loading"
          label="Your trusts"
          headline="Reading the trusts you have access to."
        />
      ) : isError ? (
        <StateCard
          variant="error"
          testId="trusts-error"
          label="Your trusts"
          headline={mapError(error, 'load')}
          detail="Nothing on this screen has been saved or changed. Reload the page to try again."
        />
      ) : trusts.length === 0 ? (
        /* ── WHO IS READING THIS? (2026-08-26) ────────────────────────────────
           Paul, 23 August, having run a client's whole ingestion: "I did the
           whole process for the bullprint client, but I don't see any trusts
           here. Absolutely nothing."

           The trusts were almost certainly never committed — "Confirm and
           create" was dead that evening (the act-as token expired mid-review
           and the whole overlay died in React state with zero requests sent),
           and it was fixed the same night, AFTER that recording. So the list
           was honestly empty.

           What was not honest was the copy. Both branches below were written
           for the CLIENT: a firm that had just spent ten minutes reading a
           deed was told its "trust specialist" would grant it access. It is
           the specialist. Posture leakage (ARC-01), and on the one screen
           where a firm most needs to know whether its work landed. */
        <StateCard
          variant="empty"
          testId="trusts-empty"
          label="Your trusts"
          headline={
            actingFor
              ? `Nothing is on file for ${actingFor} yet.`
              : canSelfOnboard
                ? 'You do not have any trusts under administration yet.'
                : 'You do not have any trusts yet.'
          }
          detail={
            actingFor
              ? 'A trust appears here once its documents have been read and you confirm the structure. You start that from the Client Book.'
              : canSelfOnboard
                ? 'Onboarding routes here once your trust deed and identity verification are accepted, or add a trust you administer now.'
                : stewardship === 'firm'
                  ? 'Trusts appear here once your trust specialist grants you access.'
                  : 'Trusts appear here once you are given access to one.'
          }
          {...(canAdd ? { action: { label: 'Add a trust', onClick: () => setAddOpen(true) } } : {})}
        />
      ) : (
        <ConsoleBand
          id="your-trusts"
          /* Not "Your trusts": that is the eyebrow two lines up, and a band
             label repeating the page's own name says nothing. This names what
             the region IS, which is the point of a band label, and it is the
             same fact the footer line spells out. */
          title="The trusts you can see"
          note={
            /* The book total, right-aligned on the band's own rule. Absent
               rather than zero when this reader may read no valuation: a total
               of R 0 over a list of trusts is a claim, not a blank. */
            readableValued.length > 0
              ? `${fmtR(bookTotal)} under administration`
              : undefined
          }
        >
          <div className="card">
            <BleedTable
              testId="trusts"
              label="Trusts you have access to"
              columns={columns}
              rows={rows}
              footer={
                <>
                  <span />
                  <span>{joinsListLine}</span>
                </>
              }
            />
          </div>
        </ConsoleBand>
      )}
    </ScreenShell>
  );
}

/** The FICA chip. Sentence case, because tracked-out caps in a narrow column is
 *  three words of noise, and the severity is already carried by the row's 2px
 *  left marker. The label itself is `ficaBadgeFor`'s, unchanged: it states the
 *  distance to the review and claims nothing about the file (TRS-01). */
function FicaChip({ badge }: { badge: FicaBadge }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 500,
        padding: '3px 9px',
        borderRadius: 999,
        background: badge.bg,
        color: badge.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {badge.label}
    </span>
  );
}

/** How many people hold a portal login on a trust, in words. */
function portalAccessPhrase(n: number): string {
  if (n === 0) return 'No one has portal access yet';
  return `${n} ${n === 1 ? 'person' : 'people'} with portal access`;
}

/** One table row for one trust. Pure: every decision is read off the trust
 *  itself plus the one list-wide flag, so a row cannot disagree with its
 *  column. */
function trustRow(trust: TrustSummary, showValueColumn: boolean): BleedRow {
  const badge = ficaBadgeFor(ficaDaysOutOf(trust), trust.initialFicaOnFile);
  const reg = registrationDisplay(trust.registration, trust.registrationDate);
  const canFinancials = canReadSurface(trust, 'financials');
  const canPeople = canReadSurface(trust, 'people');
  const memberCount = trust.memberIds?.length;

  return {
    key: trust.id,
    // The one urgency mechanism, the same 2px left edge used on rows, tiles and
    // state cards. `alarm` is due-now or overdue; a review merely upcoming is
    // normal business and takes no marker (first-touch rehearsal, finding #13).
    tone: badge.tone === 'alarm' ? 'overdue' : 'routine',
    cells: {
      trust: (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Link
            to="/app/trusts/$id"
            params={{ id: trust.id }}
            data-testid={`trust-name-${trust.id}`}
            className="bleed-table-entity"
            style={{ textDecoration: 'none' }}
          >
            {displayTrustName(trust.name)}
          </Link>
          <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            {/* ── THE PLACEHOLDER THAT NEVER LEFT THIS CARD (TRS-02) ─────────
                The trust overview stopped printing the backend's raw "Pending
                registration" on 26 August. This row, two clicks away, kept
                printing it verbatim, which is the same self-contradiction on
                the screen a client opens first. One fact, three render paths,
                one of them fixed: exactly the shape that let "Settlor" survive
                four asks. The mapping lives in lib/format/registration and
                every path reads it. */}
            <span className={reg.isNumber ? 'bleed-table-mono' : 'bleed-table-sub'}>
              {reg.text}
            </span>
            {/* `people`. Dropped when absent, not printed as nought.
                `memberIds` are the people with a PORTAL LOGIN on the trust,
                not the people on it: "0 members" beside a People tab listing a
                founder, two trustees and a beneficiary read as a contradiction
                (NEW-R12-MYTRUSTS-MEMBERS-WORD), so the line says what it
                counts, in the People tab header's own terms. */}
            {canPeople && memberCount !== undefined ? (
              /* The separator is part of this span, not a gap: two facts with
                 whitespace between them read as one run-on, which is the same
                 thing the shared row's own description line exists to stop. */
              <span className="bleed-table-sub">
                {'· '}
                {portalAccessPhrase(memberCount)}
              </span>
            ) : null}
          </span>
        </span>
      ),
      deed: trust.deedDate ? (
        <span className="bleed-table-mono">{formatDeedDate(trust.deedDate)}</span>
      ) : (
        <span className="bleed-table-sub">Not on file</span>
      ),
      // Rule 1: the cell of a trust whose financials this reader may not read
      // is not in the object at all, so `BleedTable` renders an empty cell and
      // no figure, placeholder or dash is invented for it.
      ...(showValueColumn && canFinancials
        ? { value: <ValueCell trust={trust} /> }
        : {}),
      fica: <FicaChip badge={badge} />,
      open: (
        <Link
          to="/app/trusts/$id"
          params={{ id: trust.id }}
          className="btn-secondary"
          style={{ minWidth: 76, justifyContent: 'center', textDecoration: 'none' }}
        >
          Open
        </Link>
      ),
    },
  };
}

/** The value cell: the registered total in ROMAN type (an amount is not a named
 *  thing), over the change note. */
function ValueCell({ trust }: { trust: TrustSummary }) {
  // ── Tier projection (2026-08-18) ──────────────────────────────────────────
  // The API OMITS `totalValue` / `yoyPct` / `yoyAmount` / `memberIds` for a
  // caller who may not read this trust's `financials` / `people` surfaces. The
  // guards test for ABSENCE, not for zero: a trust with a genuinely flat year
  // and a trust whose value is being withheld are different facts.
  const yoyPct = trust.yoyPct;
  const yoyColor = (yoyPct ?? 0) >= 0 ? 'var(--positive)' : 'var(--negative)';
  const yoyLabel =
    yoyPct === undefined ? '' : yoyPct >= 0 ? `+${yoyPct.toFixed(1)}%` : `${yoyPct.toFixed(1)}%`;
  // F6 (YoY zero-guard): a fresh trust has no 12-month-old data — its
  // yoyPct/yoyAmount arrive as 0/0. "+0.0% year on year" would be an invented
  // return, so the line is replaced by the honest "as at" subline (F5).
  const hasYoY =
    yoyPct !== undefined && !(yoyPct === 0 && (trust.yoyAmount ?? 0) === 0);

  return (
    <span
      data-testid={`trust-value-${trust.id}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <span
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--ink)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {trust.totalValue === undefined ? 'Not valued' : fmtR(trust.totalValue)}
      </span>
      {/* Wave-6 row 4 (Paul-decided): the SECOND figure. The value above is the
          HONEST registered/verified total; this line surfaces claimed-but-not-
          yet-registered asset value SEPARATELY, never folded into the total.
          Zero-suppressed — absent when nothing is pending. */}
      {(trust.claimedPendingValue ?? 0) > 0 ? (
        <span
          data-testid="trust-claimed-pending"
          style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}
        >
          + {fmtR(trust.claimedPendingValue ?? 0)} claimed, pending registration
        </span>
      ) : null}
      {hasYoY ? (
        <span style={{ fontSize: 12, color: yoyColor, fontWeight: 500 }}>
          {yoyLabel} year on year
        </span>
      ) : null}
      {/* F5: the total is a dated fact — "as at" plus (when the Wave-1 backend
          stamps it) whether it is an AFS NAV or the live holdings sum. */}
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--ink-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        as at {formatAsAtShort(trust.lastUpdated)}
        {trust.totalValueBasis ? (
          <ValueProvenance
            label={trust.totalValueBasis === 'afs' ? 'AFS valuation' : 'Live holdings sum'}
            compact
          />
        ) : null}
      </span>
    </span>
  );
}
